"use client";

/**
 * ESQUECI MINHA SENHA — a tela de `/auth/forgot-password`, no desenho do
 * sistema. Até 23/10/2026 ela era um parágrafo dizendo que a recuperação não
 * existia e que a pessoa falasse com o administrador; agora ela FAZ o pedido.
 *
 * Dois caminhos, e a tela diz qual é o seu: e-mail de verdade recebe o link;
 * cadastro interno (`nome@alliance`) acende o sino dos administradores, que
 * redefinem na janela "Definir senha" de Configurações › Equipe e permissões ›
 * Usuários. A regra de qual caminho é a de `lib/auth/password-reset.ts`, e
 * sai só do que foi digitado: a tela responde a mesma coisa para um login que
 * existe e para um que não existe.
 *
 * `preview`: nada vai ao servidor — a tela escolhe o caminho pela mesma régua
 * pura e mostra o resultado.
 */
import { useState } from "react";
import Link from "next/link";
import { ArrowRight, CircleAlert, KeyRound, Loader2, Mail } from "lucide-react";

import Banner from "@/components/ds/Banner";
import Button from "@/components/ds/Button";
import { Field, TextInput } from "@/components/ds/Field";
import { requestPasswordReset } from "@/lib/auth/password-reset-actions";
import { normalizeLogin, resetPathFor, type ResetPath } from "@/lib/auth/password-reset";

export default function ForgotPasswordScreen({
  preview = false,
}: {
  preview?: boolean;
}) {
  const [login, setLogin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<ResetPath | null>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const value = normalizeLogin(login);
    if (!value) {
      setError("Digite o e-mail ou usuário com que você entra.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const r = preview
        ? { path: resetPathFor(value) }
        : await requestPasswordReset(value);
      setDone(r.path);
    } catch {
      setError("Não foi possível enviar o pedido. Tente de novo.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <h1 className="text-2xl font-extrabold tracking-caps text-ink uppercase">
        Esqueci minha senha
      </h1>

      {done === null ? (
        <>
          <p className="mt-1 text-sm text-ink-2">
            Digite o login com que você entra. Se ele for um e-mail de verdade,
            mandamos um link para escolher a senha nova; se for um cadastro
            interno, pedimos ao administrador da sua construtora.
          </p>

          <form onSubmit={handleSubmit} className="mt-7 flex flex-col gap-4">
            <Field
              label="E-mail ou usuário"
              htmlFor="login"
              hint="O mesmo que você digita na tela de entrada."
            >
              <div className="relative">
                <Mail
                  className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-2"
                  aria-hidden
                />
                <TextInput
                  id="login"
                  type="text"
                  inputMode="email"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  required
                  value={login}
                  onChange={(e) => setLogin(e.target.value)}
                  disabled={loading}
                  placeholder="voce@empresa.com.br"
                  className="w-full pl-9"
                />
              </div>
            </Field>

            {error && (
              <div role="alert">
                <Banner family="red" icon={CircleAlert} title={error} />
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={loading}
              className="w-full"
            >
              {loading ? (
                <Loader2 className="animate-spin" aria-hidden />
              ) : (
                <>
                  Continuar
                  <ArrowRight aria-hidden />
                </>
              )}
            </Button>
          </form>
        </>
      ) : (
        <div className="mt-6 flex flex-col gap-4" role="status">
          {done === "email" ? (
            <Banner
              family="green"
              icon={Mail}
              title="Se este login existir, o link está a caminho"
              description="Abra o e-mail e escolha a senha nova. O link vale por 1 hora e só pode ser usado uma vez. Não chegou em alguns minutos? Veja o lixo eletrônico ou fale com o administrador da sua construtora."
            />
          ) : (
            <Banner
              family="blue"
              icon={KeyRound}
              title="Pedimos ao administrador"
              description="Este login não é um e-mail de verdade, então não dá para mandar o link por e-mail. Avisamos os administradores da sua construtora: eles definem uma senha nova em Configurações › Equipe e permissões › Usuários e combinam ela com você."
            />
          )}
          <button
            type="button"
            onClick={() => {
              setDone(null);
              setError(null);
            }}
            className="min-h-11 self-start text-sm font-semibold text-action hover:underline md:min-h-0"
          >
            Digitar outro login
          </button>
        </div>
      )}

      <Link
        href="/auth/login"
        className="mt-6 inline-flex min-h-11 items-center self-start text-sm font-semibold text-action hover:underline md:min-h-0"
      >
        ‹ Voltar para o login
      </Link>
    </div>
  );
}
